import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';
import { ReportsService } from '../../services/reports.service';

export interface CommissionedSalesRow {
	trandate: string;
	documentLabel: string;
	customerId: string;
	customerName: string;
	description: string;
	lineId: string;
	lineDescription: string;
	categoryName: string;
	mfgPart: string;
	stockId: string;
	typeName: string;
	unitPriceAfterDiscount: number;
	qty: number;
	standardUnitCost: number;
	lineSales: number;
	lineCost: number;
	gp: number;
	gpPctDisplay: number;
	commissionPct: number;
	commissionAmount: number;
	salespersonName: string;
}

@Component({
	selector: 'app-commission-sales-report',
	templateUrl: './commission-sales-report.component.html',
	styleUrls: ['./commission-sales-report.component.scss']
})
export class CommissionSalesReportComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(MatSort, { static: false }) sort: MatSort;

	color = 'blue';
	config: any;
	sending = false;
	metaLoaded = false;
	productLines: { lineid: string; linedescription: string }[] = [];
	suppliers: { supplierid: string; suppname: string }[] = [];
	locations: { loccode: string; locationname: string }[] = [];
	categories: { categoryid: string; name: string }[] = [];
	salespeople: any[] = [];

	fromdateCtrl = new UntypedFormControl(null);
	todateCtrl = new UntypedFormControl(null);
	productLineRef = new UntypedFormControl('');
	supplierRef = new UntypedFormControl('');
	locationRef = new UntypedFormControl('');
	categoryRef = new UntypedFormControl('');
	salespersonRef = new UntypedFormControl('');
	keywordsRef = new UntypedFormControl('');
	customerInfoRef = new UntypedFormControl('');
	groupByCustomerRef = new UntypedFormControl(false);

	reportCriteria: any = null;
	totals: any = null;
	resultSub: Subscription;
	metaSub: Subscription;
	spSub: Subscription;

	dataSource = new MatTableDataSource<CommissionedSalesRow>([]);
	displayedColumns: string[] = [
		'trandate',
		'documentLabel',
		'customerId',
		'customerName',
		'description',
		'lineId',
		'lineDescription',
		'categoryName',
		'mfgPart',
		'stockId',
		'typeName',
		'unitPriceAfterDiscount',
		'qty',
		'standardUnitCost',
		'lineSales',
		'lineCost',
		'gp',
		'gpPctDisplay',
		'commissionPct',
		'commissionAmount',
		'salespersonName'
	];

	filename = 'Commissioned_Sales_';

	constructor(
		private globalSearchService: GlobalSearchService,
		private reportsService: ReportsService,
		private printService: PrintService,
		private locationSvc: Location,
		private cdr: ChangeDetectorRef
	) {
		this.color = this.globalSearchService.getColor();
		this.globalSearchService.mesagesallowed.next(false);
		this.globalSearchService.aiDataAvailable$.next(null);
		this.globalSearchService.configsubscription.subscribe((r) => {
			this.config = r;
		});
	}

	ngOnInit(): void {
		const end = new Date();
		const start = new Date(end.getFullYear(), end.getMonth() - 1, 1);
		this.fromdateCtrl.setValue(start);
		this.todateCtrl.setValue(end);

		this.metaSub = this.reportsService.getCommissionedSalesMeta().subscribe({
			next: (res: any) => {
				if (res && res.success !== false) {
					this.productLines = res.productLines || [];
					this.suppliers = res.suppliers || [];
					this.locations = res.locations || [];
					this.categories = res.categories || [];
				}
				this.metaLoaded = true;
				this.cdr.markForCheck();
			},
			error: () => {
				this.metaLoaded = true;
			}
		});

		this.spSub = this.reportsService.getSalesPeople().subscribe((rows: any) => {
			this.salespeople = rows || [];
		});
	}

	ngAfterViewInit(): void {
		this.attachSort();
	}

	ngOnDestroy(): void {
		this.resultSub?.unsubscribe();
		this.metaSub?.unsubscribe();
		this.spSub?.unsubscribe();
	}

	private attachSort(): void {
		if (this.sort) {
			this.dataSource.sort = this.sort;
		}
	}

	back(): void {
		this.locationSvc.back();
	}

	documentParts(label: string): { main: string; sub: string } {
		if (!label) {
			return { main: '', sub: '' };
		}
		const sep = ' / ';
		const i = label.indexOf(sep);
		if (i < 0) {
			return { main: label, sub: '' };
		}
		return { main: label.slice(0, i).trim(), sub: label.slice(i + sep.length).trim() };
	}

	private formatDateCtrl(v: any): string {
		if (!v) {
			return '';
		}
		const d = v instanceof Date ? v : new Date(v);
		if (Number.isNaN(d.getTime())) {
			return '';
		}
		return d.toISOString().split('T')[0];
	}

	runReport(): void {
		const payload = {
			datefrom: this.formatDateCtrl(this.fromdateCtrl.value),
			dateto: this.formatDateCtrl(this.todateCtrl.value),
			productline: this.productLineRef.value || '',
			supplier: this.supplierRef.value || '',
			location: this.locationRef.value || '',
			category: this.categoryRef.value || '',
			salesperson: this.salespersonRef.value || '',
			keywords: (this.keywordsRef.value || '').trim(),
			customerinfo: (this.customerInfoRef.value || '').trim(),
			groupByCustomerStock: !!this.groupByCustomerRef.value
		};

		this.reportCriteria = payload;
		this.sending = true;
		this.totals = null;
		this.dataSource.data = [];

		this.resultSub?.unsubscribe();
		this.resultSub = this.reportsService.getCommissionedSales(payload).subscribe({
			next: (r: any) => {
				this.sending = false;
				if (!r?.success) {
					this.totals = null;
					this.dataSource.data = [];
					this.globalSearchService.aiDataAvailable$.next(null);
					return;
				}
				this.totals = r.totals;
				this.dataSource.data = r.rows || [];
				this.globalSearchService.aiDataAvailable$.next(r);
				this.cdr.detectChanges();
				this.attachSort();
			},
			error: () => {
				this.sending = false;
			}
		});
	}

	getReportSubtitle(): string {
		if (!this.reportCriteria) {
			return '';
		}
		const p: string[] = [];
		if (this.reportCriteria.productline) {
			const pl = this.productLines.find((x) => x.lineid === this.reportCriteria.productline);
			if (pl) {
				p.push('Product line: ' + pl.linedescription);
			}
		}
		if (this.reportCriteria.salesperson) {
			const sp = this.salespeople.find((x) => x.salesmancode === this.reportCriteria.salesperson);
			if (sp) {
				p.push('Salesperson: ' + sp.salesmanname);
			}
		}
		if (this.reportCriteria.supplier) {
			const s = this.suppliers.find((x) => x.supplierid === this.reportCriteria.supplier);
			if (s) {
				p.push('Vendor: ' + s.suppname);
			}
		}
		if (this.reportCriteria.location) {
			const l = this.locations.find((x) => x.loccode === this.reportCriteria.location);
			if (l) {
				p.push('Location: ' + l.locationname);
			}
		}
		if (this.reportCriteria.category) {
			const c = this.categories.find((x) => x.categoryid === this.reportCriteria.category);
			if (c) {
				p.push('Category: ' + c.name);
			}
		}
		if (this.reportCriteria.keywords) {
			p.push('Keywords: ' + this.reportCriteria.keywords);
		}
		if (this.reportCriteria.customerinfo) {
			p.push('Customer filter: ' + this.reportCriteria.customerinfo);
		}
		if (this.reportCriteria.groupByCustomerStock) {
			p.push('Grouped by customer + stock');
		}
		return p.join(' · ');
	}

	generateExportTableHtml(): string {
		const rows = this.dataSource.data;
		if (!rows.length) {
			return '';
		}
		const esc = (s: string) =>
			String(s ?? '')
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;');
		const money = (n: number) =>
			(n ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
		const num = (n: number, d = 2) => (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

		let html = `<table class="table"><thead><tr>
			<th>Date</th><th>Document #</th><th>Doc type</th><th>Cust ID</th><th>Customer</th><th>Description</th>
			<th>Line</th><th>Line desc</th><th>Category</th><th>MFG #</th><th>Stock ID</th>
			<th style="text-align:right">Sale price</th><th style="text-align:right">Qty</th><th style="text-align:right">Std cost</th>
			<th style="text-align:right">Sales</th><th style="text-align:right">Line cost</th><th style="text-align:right">GP</th>
			<th style="text-align:right">GP %</th><th style="text-align:right">Comm %</th><th style="text-align:right">Commission</th><th>Salesperson</th>
		</tr></thead><tbody>`;

		for (const line of rows) {
			const doc = this.documentParts(line.documentLabel);
			const docType = doc.sub || line.typeName || '';
			html += `<tr>
				<td>${esc(line.trandate)}</td>
				<td>${esc(doc.main)}</td>
				<td>${esc(docType)}</td>
				<td>${esc(line.customerId)}</td>
				<td>${esc(line.customerName)}</td>
				<td>${esc(line.description)}</td>
				<td>${esc(line.lineId)}</td>
				<td>${esc(line.lineDescription)}</td>
				<td>${esc(line.categoryName)}</td>
				<td>${esc(line.mfgPart)}</td>
				<td>${esc(line.stockId)}</td>
				<td style="text-align:right">${num(line.unitPriceAfterDiscount, 4)}</td>
				<td style="text-align:right">${num(line.qty, 4)}</td>
				<td style="text-align:right">${money(line.standardUnitCost)}</td>
				<td style="text-align:right">${money(line.lineSales)}</td>
				<td style="text-align:right">${money(line.lineCost)}</td>
				<td style="text-align:right">${money(line.gp)}</td>
				<td style="text-align:right">${num(line.gpPctDisplay, 2)}</td>
				<td style="text-align:right">${num(line.commissionPct, 1)}</td>
				<td style="text-align:right">${money(line.commissionAmount)}</td>
				<td>${esc(line.salespersonName)}</td>
			</tr>`;
		}

		html += '</tbody>';
		if (this.totals) {
			html += `<tfoot><tr>
				<td colspan="4">Customers: ${this.totals.distinctCustomers}</td>
				<td colspan="7"></td>
				<td colspan="2" style="text-align:right"><strong>Totals</strong></td>
				<td style="text-align:right"><strong>${money(this.totals.lineSales)}</strong></td>
				<td style="text-align:right"><strong>${money(this.totals.lineCost)}</strong></td>
				<td style="text-align:right"><strong>${money(this.totals.gp)}</strong></td>
				<td></td>
				<td></td>
				<td style="text-align:right"><strong>${money(this.totals.commission)}</strong></td>
				<td></td>
			</tr></tfoot>`;
		}
		html += '</table>';
		return html;
	}

	exportPdf(): void {
		if (!this.dataSource.data.length) {
			return;
		}
		this.sending = true;
		const now = new Date();
		const encoded = this.globalSearchService.base64encode(this.generateExportTableHtml());
		const data = {
			content: encoded,
			filename: this.filename + (now.getMonth() + 1) + now.getDate() + now.getFullYear(),
			title: `Commissioned Sales ${this.reportCriteria.datefrom} – ${this.reportCriteria.dateto} @ ${now.toLocaleString()}`,
			subtitle: this.getReportSubtitle()
		};
		this.printService.pdf(data).subscribe({
			next: (result: any) => {
				this.sending = false;
				this.globalSearchService.downloadPdf(result.content, data.filename);
			},
			error: () => {
				this.sending = false;
			}
		});
	}

	exportXls(): void {
		if (!this.dataSource.data.length) {
			return;
		}
		this.sending = true;
		const now = new Date();
		const encoded = this.globalSearchService.base64encode(this.generateExportTableHtml());
		const data = {
			content: encoded,
			filename: this.filename + (now.getMonth() + 1) + now.getDate() + now.getFullYear(),
			title: `Commissioned Sales ${this.reportCriteria.datefrom} ${this.reportCriteria.dateto} @ ${now.toLocaleString()}`,
			subtitle: this.getReportSubtitle()
		};
		this.printService.xls(data).subscribe({
			next: (result: any) => {
				this.sending = false;
				this.globalSearchService.downloadXls(result.content, data.filename);
			},
			error: () => {
				this.sending = false;
			}
		});
	}
}
